/**
 * Cart shipping display. Amounts come only from the verified profile rules that
 * Liquid renders into [data-cart-estimate]; otherwise checkout calculates them.
 * Never call Shopify's cart shipping-rate endpoints (prepare/async rates or the
 * rates JSON): Shopify stores the posted address on the cart and prefills
 * checkout with it (reported reference-postcode incident 2026-10-23).
 */
export function taxTotals({ reduced, standard, exempt = 0, reducedRate, standardRate }, shipping) {
  const reducedTax = Math.round(reduced * reducedRate / (100 + reducedRate));
  const standardTax = Math.round((standard + shipping) * standardRate / (100 + standardRate));
  return { reducedTax, standardTax, net: reduced + standard + exempt + shipping - reducedTax - standardTax };
}

export function shippingDisplayState(state, quote, { country, currency, subscription = false } = {}) {
  if (country !== 'DE' || currency !== 'EUR' || subscription) return { status: 'unknown' };
  // Only verified one-time cart rules provide a quote. Subscription shipping
  // stays in checkout, which knows the actual initial and recurring groups.
  if (state.status === 'unknown' && state.reason !== 'conflict' && ['profile', 'cart-total'].includes(quote.policy) &&
      Number.isFinite(quote.expiresAt) && quote.expiresAt * 1000 > Date.now() &&
      Number.isSafeInteger(quote.standardCents) && quote.standardCents >= 0) {
    return { status: 'ready', cents: quote.freeEligible ? 0 : quote.standardCents, name: '' };
  }
  return { status: state.status === 'ready' ? 'unknown' : state.status };
}

function quoteConfig(data) {
  return { policy: data.shippingQuotePolicy || 'checkout', standardCents: Number(data.shippingStandardCents),
    freeEligible: data.shippingFreeEligible === 'true', expiresAt: Number(data.shippingQuoteExpires) };
}

function money(cents, data) {
  return new Intl.NumberFormat(data.locale || 'de', { style: 'currency', currency: data.currency || 'EUR' }).format(cents / 100);
}

export function nextTier(raw, count) {
  try {
    const tiers = JSON.parse(raw);
    if (!Array.isArray(tiers)) return null;
    return tiers.filter((tier) => Number.isInteger(tier.min) && tier.min > count
      && Number.isFinite(tier.percent) && tier.percent > 0 && tier.percent < 100)
      .sort((a, b) => a.min - b.min)[0] || null;
  } catch { return null; }
}

function renderTierHints() {
  if (window.QuantityDiscountPreview) window.QuantityDiscountPreview.renderCartHints();
}

const quoteTimers = new WeakMap();
const progressTimers = new WeakMap();

function applyState(state) {
  renderTierHints();
  document.querySelectorAll('[data-cart-estimate]').forEach((summary) => {
    const data = summary.dataset;
    const quote = quoteConfig(data);
    const display = shippingDisplayState(state, quote, { country: data.country, currency: data.currency, subscription: data.hasSubscription === 'true' });
    const quoted = display.status === 'ready';
    const ruleCopy = summary.querySelector('[data-cart-shipping-rule-copy]');
    if (ruleCopy) ruleCopy.hidden = !quoted || quote.policy !== 'cart-total';
    clearTimeout(quoteTimers.get(summary));
    quoteTimers.delete(summary);
    if (quoted) {
      const timer = setTimeout(() => {
        if (summary.isConnected && quoteTimers.get(summary) === timer) {
          applyState({ status: 'unknown' });
          document.dispatchEvent(new CustomEvent('cart:shipping-expired'));
        }
      }, Math.min(2147483647, quote.expiresAt * 1000 - Date.now() + 10));
      quoteTimers.set(summary, timer);
    }
    // When the merchant hides shipping costs, don't silently include shipping.
    const shipping = quoted && summary.querySelector('[data-shipping-cost-value]') ? display.cents : 0;
    const total = Number(data.goodsTotal) + shipping;
    const format = (value) => money(value, data);
    const value = summary.querySelector('[data-shipping-cost-value]');
    if (value) value.textContent = quoted ? (display.cents === 0 ? data.textFree : format(display.cents)) : data.textUnknown;
    const shippingLabel = summary.querySelector('[data-shipping-cost-label]');
    if (shippingLabel) shippingLabel.textContent = quoted ? data.textQuotedLabel : data.textShippingLabel;
    const caption = summary.querySelector('[data-shipping-estimate-status]');
    if (caption) {
      caption.hidden = !quoted;
      caption.textContent = quoted && value ? `${data.textQuotedLabel || data.textShippingLabel || ''}: ${value.textContent}` : '';
    }
    const surface = summary.closest('[data-cart-page], [data-cart-drawer]');
    surface?.querySelectorAll('[data-shipping-rule-progress]').forEach((progress) => {
      const remaining = Number(progress.dataset.shippingRuleExpires) * 1000 - Date.now();
      clearTimeout(progressTimers.get(progress));
      progressTimers.delete(progress);
      progress.hidden = state.status === 'changing' || data.country !== 'DE' || data.currency !== 'EUR' ||
        data.hasSubscription === 'true' || !Number.isFinite(remaining) || remaining <= 0 || quoted && display.cents === 0;
      if (!progress.hidden) {
        const timer = setTimeout(() => {
          if (!progress.isConnected || progressTimers.get(progress) !== timer) return;
          progress.hidden = true;
          const host = progress.closest('[data-shipping-progress-host]');
          if (host) host.hidden = true;
          document.dispatchEvent(new CustomEvent('cart:shipping-expired'));
        }, Math.min(2147483647, remaining + 10));
        progressTimers.set(progress, timer);
      }
    });
    surface?.querySelectorAll('[data-shipping-progress-host]').forEach(host => {
      host.hidden = !host.querySelector('[data-shipping-rule-progress]:not([hidden])');
    });
    surface?.querySelectorAll('[data-shipping-notice]').forEach((notice) => {
      notice.textContent = quoted ? (display.cents === 0 ? (data.textFreeNotice || data.textFree) : notice.dataset.textQuote.replace('__AMOUNT__', format(display.cents))) : data.textUnknown;
    });
    surface?.querySelectorAll('[data-cart-total], [data-checkout-total], [data-cart-sticky-total]').forEach((element) => {
      element.textContent = format(total);
    });
    surface?.querySelectorAll('[data-shipping-notice-mini]').forEach((element) => {
      element.textContent = quoted && value ? (display.cents === 0 ? (data.textFreeIncluded || data.textIncluded) : data.textIncluded) : data.textShippingExcluded;
    });
    const savings = quoted && display.cents === 0 ? quote.standardCents : 0;
    const savingsRow = summary.querySelector('[data-cart-savings]');
    if (savingsRow) {
      const amount = (Number(data.goodsSavings) || 0) + savings;
      savingsRow.hidden = amount <= 0;
      savingsRow.querySelector('[data-cart-savings-value]').textContent = format(amount);
      const detail = savingsRow.querySelector('[data-cart-shipping-savings]');
      detail.hidden = savings <= 0;
      detail.textContent = (data.textShippingSavings || '').replace('__AMOUNT__', format(savings));
    }
    const vat = summary.querySelector('[data-cart-vat-summary]');
    if (vat) {
      const tax = taxTotals({ reduced: Number(vat.dataset.reducedGross), standard: Number(vat.dataset.standardGross),
        exempt: Number(vat.dataset.exemptGross), reducedRate: Number(vat.dataset.reducedRate), standardRate: Number(vat.dataset.standardRate) }, shipping);
      vat.querySelector('[data-vat-net]').textContent = format(tax.net);
      vat.querySelector('[data-vat-reduced]').textContent = format(tax.reducedTax);
      vat.querySelector('[data-vat-standard]').textContent = format(tax.standardTax);
      vat.querySelector('[data-vat-standard]').closest('[data-vat-rate]').hidden = Number(vat.dataset.standardGross) + shipping === 0;
    }
  });
}

if (typeof document !== 'undefined') {
  // 'changing' while a cart mutation runs, otherwise 'unknown' (rules decide).
  let shippingState = { status: 'unknown' };
  const setShippingState = (status) => { shippingState = { status }; applyState(shippingState); };
  let ruleRefresh;
  let ruleRetry;
  let ruleAttempts = 0;
  let cartGeneration = 0;
  const canRecoverRules = summary => {
    if (!summary || shippingState.status === 'changing') return false;
    const data = summary.dataset;
    const progress = summary.closest('[data-cart-page], [data-cart-drawer]')?.querySelector('[data-shipping-rule-progress]');
    const progressExpired = progress && Number(progress.dataset.shippingRuleExpires) * 1000 <= Date.now();
    return Number(data.itemCount) > 0 && data.country === 'DE' && data.currency === 'EUR' &&
      data.hasSubscription !== 'true' &&
      (progressExpired || data.shippingRuleRecovery === 'true' ||
      ['profile', 'cart-total'].includes(data.shippingQuotePolicy) && Number(data.shippingQuoteExpires) * 1000 <= Date.now());
  };
  const refreshRules = async () => {
    if (ruleRefresh) return ruleRefresh;
    if (ruleAttempts >= 3) return;
    clearTimeout(ruleRetry);
    const original = document?.querySelector('[data-cart-estimate]');
    if (!canRecoverRules(original)) return;
    ruleAttempts += 1;
    const generation = cartGeneration;
    const contextKeys = ['cartSignature', 'country', 'currency', 'hasSubscription', 'rootUrl'];
    const context = Object.fromEntries(contextKeys.map(key => [key, original.dataset[key]]));
    const matchesContext = summary => summary && contextKeys.every(key => summary.dataset[key] === context[key]);
    const root = original.dataset.rootUrl || '/';
    const isCurrent = () => generation === cartGeneration && shippingState.status !== 'changing' &&
      matchesContext(document?.querySelector('[data-cart-estimate]'));
    const retry = () => {
      if (ruleAttempts < 3 && isCurrent() && canRecoverRules(document?.querySelector('[data-cart-estimate]'))) {
        ruleRetry = setTimeout(refreshRules, 30000);
      }
    };
    ruleRefresh = (async () => {
      let deadline;
      const abort = new AbortController();
      try {
        const sectionsRequest = (async () => {
          const response = await fetch(`${root}?sections=cart&_=${Date.now()}`, { cache: 'no-store', signal: abort.signal });
          return { response, sections: response.ok ? await response.json() : null };
        })();
        const { response, sections } = await Promise.race([sectionsRequest, new Promise((_, reject) => {
          deadline = setTimeout(() => { abort.abort(); reject(new Error('Shipping display refresh expired')); }, 6000);
        })]);
        if (!isCurrent()) return;
        if (!response.ok) {
          if (response.status >= 500 || response.status === 408 || response.status === 429) retry();
          else ruleAttempts = 3;
          return;
        }
        if (!sections || typeof sections.cart !== 'string' || !isCurrent()) { retry(); return; }
        const doc = new DOMParser().parseFromString(sections.cart, 'text/html');
        const fresh = doc.querySelector('[data-cart-estimate]');
        if (!matchesContext(fresh)) return;
        const freshProgress = doc.querySelector('[data-shipping-rule-progress]');
        const validProgress = freshProgress && Number(freshProgress.dataset.shippingRuleExpires) * 1000 > Date.now();
        document.querySelectorAll('[data-cart-estimate]').forEach(summary => {
          if (!matchesContext(summary)) return;
          for (const key of ['shippingQuotePolicy', 'shippingStandardCents', 'shippingFreeEligible', 'shippingQuoteExpires', 'shippingRuleRecovery']) summary.dataset[key] = fresh.dataset[key];
          const surface = summary.closest('[data-cart-page], [data-cart-drawer]');
          const previous = surface?.querySelector('[data-shipping-rule-progress]');
          if (previous) {
            clearTimeout(progressTimers.get(previous));
            progressTimers.delete(previous);
            previous.remove();
          }
          const host = surface?.querySelector('[data-shipping-progress-host]');
          const notice = surface?.querySelector('[data-cart-shipping-hint] [data-shipping-notice]');
          if (validProgress && host) host.append(freshProgress.cloneNode(true));
          else if (validProgress && notice) notice.before(freshProgress.cloneNode(true));
        });
        refresh();
        if (shippingDisplayState(shippingState, quoteConfig(fresh.dataset), context).status === 'ready' || validProgress) ruleAttempts = 0;
        else retry();
      } catch { retry(); }
      finally { clearTimeout(deadline); }
    })().finally(() => {
      ruleRefresh = null;
      if (generation !== cartGeneration && canRecoverRules(document?.querySelector('[data-cart-estimate]'))) void refreshRules();
    });
    return ruleRefresh;
  };
  let observedBar;
  const barObserver = typeof ResizeObserver === 'function' ? new ResizeObserver((entries) => {
    const height = entries[0]?.target.getBoundingClientRect().height;
    if (height > 0) document.documentElement.style.setProperty('--cart-sticky-height', `${height}px`);
  }) : null;
  const refresh = () => {
    const bar = document.querySelector('[data-cart-sticky-bar]');
    document.body.classList.toggle('cart-sticky-bar-visible', !!bar);
    if (bar !== observedBar) {
      barObserver?.disconnect();
      if (bar) barObserver?.observe(bar);
      observedBar = bar;
    }
    const summary = document.querySelector('[data-cart-estimate]');
    // Section rendering must not release a mutation that is still in flight.
    applyState(shippingState);
    if (canRecoverRules(summary)) void refreshRules();
  };
  document.addEventListener('cart:shipping-expired', refreshRules);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { ruleAttempts = 0; void refreshRules(); } });
  const changing = () => { cartGeneration += 1; clearTimeout(ruleRetry); setShippingState('changing'); };
  document.addEventListener('cart:changing', changing);
  document.addEventListener('cart:add', event => {
    if (event.detail?.cartRefreshed !== true) changing();
  });
  document.addEventListener('cart:updated', () => {
    cartGeneration += 1;
    clearTimeout(ruleRetry);
    ruleAttempts = 0;
    shippingState = { status: 'unknown' };
    refresh();
  });
  document.addEventListener('shopify:section:load', refresh);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
}
